// Dream Vision ("Imagine") — the generation half of the feature. The gallery half lives in
// ./visions.ts, and the usage endpoint is shared between them, so it is re-exported here rather than
// written twice: a screen that generates should only need to import one module.
//
// Two things about this API shape the client. First, generation is synchronous — the POST does not
// return until OpenAI has produced the image and the Worker has stored it, which is tens of seconds,
// so nothing here polls and every caller must be prepared to wait. Second, the image is NOT
// encrypted, unlike everything else the app stores: it comes back as a short-lived signed URL that an
// <img src> can use directly, and that stops working about fifteen minutes later. Persisting one is
// therefore always a bug.

import { ApiError, apiPost } from './client';

/** Matches DREAM_VISION_PROMPT_MAX_WORDS in the Worker, which clamps anything longer. */
export const DREAM_VISION_PROMPT_MAX_WORDS = 250;

// The usage endpoint and its `canGenerate` helper already exist for the gallery. Re-exported so a
// caller doing the whole flow imports one module, and so tests have one module to mock.
export {
  canGenerate,
  defaultVisionStyle,
  fetchVisionUsage,
  type VisionStyle,
  type VisionUsage,
} from './visions';

/* ── Stage one: the de-identified prompt ─────────────────────────────────────────────────────── */

export interface DreamVisionPrompt {
  /** The de-identified visual description. This, and only this, is what leaves for the provider. */
  prompt: string;
  /** Kinds of identifying detail the deterministic pass stripped, e.g. ['date', 'address']. */
  removed: string[];
  removedCount: number;
}

/**
 * Ask the Worker to rewrite a dream entry into a de-identified visual description.
 *
 * Free — it consumes no Dream Vision quota — but rate limited to 60/hour, because it runs Workers AI.
 * The raw entry text goes to Cloudflare's own model here and no further; the returned prompt is what
 * the second stage sends to OpenAI, after the user has approved it.
 */
export async function requestDreamVisionPrompt(
  dreamText: string,
  entryId?: string | null,
  signal?: AbortSignal,
): Promise<DreamVisionPrompt> {
  const res = await apiPost<{ prompt?: string; removed?: string[]; removedCount?: number }>(
    '/api/dream-vision/prompt',
    entryId ? { dreamText, entryId } : { dreamText },
    signal,
  );

  const removed = Array.isArray(res.removed) ? res.removed : [];
  return {
    prompt: String(res.prompt ?? ''),
    removed,
    removedCount: Number(res.removedCount ?? removed.length),
  };
}

/* ── Stage two: the image ────────────────────────────────────────────────────────────────────── */

export interface DreamVisionGeneration {
  generationId: string;
  /** Groups an original with its recreations. Passed back as `recreateOf` to stay in the session. */
  sessionId: string;
  /** Short-lived (~15 min). Usable directly as an <img src>. Never persist it. */
  signedUrl: string;
  /** Credits left this billing month. Always 0 for a free account. */
  remaining: number;
  resetsAt: string | null;
  /** True while the session's one included recreation is still unused. */
  freeRecreationAvailable: boolean;
  /** The house style it was rendered in. Empty against a Worker that predates styles. */
  style: string;
}

export interface GenerateDreamVisionInput {
  approvedPrompt: string;
  entryId?: string | null;
  /** The generation this recreates. Keeps it in the same session, where the first redo is free. */
  recreateOf?: string | null;
  /**
   * The house style to render in. Left unset by every caller today, and deliberately so: the Worker
   * falls back to its own default for a new vision and to the session's own style for a recreation,
   * which is exactly what the interface shows in both cases. Naming it here would only start being
   * necessary once there is a picker, and naming it on a recreation would silently override the
   * inheritance that keeps a redo looking like the thing it redoes.
   */
  style?: string | null;
  /** Reuse this ID when retrying the same operation. */
  requestId?: string;
}

/**
 * Generate the image. Resolves only once it exists — expect 15–30 seconds.
 *
 * A failure inside the image provider consumes no credit (the Worker writes nothing to the ledger
 * unless it has bytes), so a retry after a 502 is genuinely free and can be offered as such.
 */
export async function generateDreamVision(
  input: GenerateDreamVisionInput,
  signal?: AbortSignal,
): Promise<DreamVisionGeneration> {
  const body: Record<string, unknown> = { approvedPrompt: input.approvedPrompt,requestId:input.requestId??crypto.randomUUID() };
  if (input.entryId) body.entryId = input.entryId;
  if (input.recreateOf) body.recreateOf = input.recreateOf;
  if (input.style) body.style = input.style;

  const res = await apiPost<{
    generationId?: string;
    sessionId?: string;
    signedUrl?: string;
    remaining?: number;
    resetsAt?: string | null;
    freeRecreationAvailable?: boolean;
    style?: string;
  }>('/api/dream-vision/generate', body, signal);

  return {
    generationId: String(res.generationId ?? ''),
    sessionId: String(res.sessionId ?? ''),
    signedUrl: String(res.signedUrl ?? ''),
    remaining: Number(res.remaining ?? 0),
    resetsAt: res.resetsAt ?? null,
    freeRecreationAvailable: res.freeRecreationAvailable === true,
    style: String(res.style ?? ''),
  };
}

/**
 * Fetch the generated PNG so it can be re-encrypted into the entry's photos.
 *
 * Separate from the <img> that displays it: the browser will happily paint a cross-origin image it
 * cannot let script read, so getting at the bytes is its own request and its own failure mode.
 */
export async function fetchVisionImage(signedUrl: string, signal?: AbortSignal): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(signedUrl, { signal });
  } catch {
    throw new ApiError(0, 'Could not download the image. Check your connection and try again.');
  }
  if (!res.ok) throw new ApiError(res.status, 'That image link has expired. Recreate the vision to get a fresh one.');
  return res.blob();
}

/* ── Prompt arithmetic ───────────────────────────────────────────────────────────────────────── */

export function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function clampPromptWords(text: string, max: number = DREAM_VISION_PROMPT_MAX_WORDS): string {
  const words = text.trim().split(/\s+/).filter(Boolean);
  return words.length <= max ? words.join(' ') : words.slice(0, max).join(' ');
}

/**
 * The prompt for a recreation: the approved description plus what the user wants changed.
 *
 * Appended rather than merged so the approval still means something — the user can see that the
 * description they signed off on is unaltered, with their instruction added underneath.
 *
 * When the pair would breach the word cap it is the description that gives way, not the
 * instruction. The Worker clamps from the end, so leaving it to do the trimming would delete the
 * one part the user just asked for and quietly regenerate the same image.
 */
export function buildRefinedPrompt(approvedPrompt: string, chips: string[], instruction: string): string {
  const request = [...chips, instruction]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ');
  if (!request) return approvedPrompt;

  const suffix = `\n\nRefinement request: ${request}`;
  const budget = DREAM_VISION_PROMPT_MAX_WORDS - countWords(suffix);
  const base =
    countWords(approvedPrompt) > budget ? clampPromptWords(approvedPrompt, Math.max(0, budget)) : approvedPrompt;

  return `${base}${suffix}`;
}

/* ── Failures ────────────────────────────────────────────────────────────────────────────────── */

export type DreamVisionFailure =
  | 'paywall'
  | 'style_locked'
  | 'unknown_style'
  | 'monthly_limit'
  | 'daily_limit'
  | 'rate_limited'
  | 'unconfigured'
  | 'provider'
  | 'missing_source'
  | 'offline'
  | 'unknown';

function bodyField(err: ApiError, key: string): string | null {
  const body = err.body;
  if (!body || typeof body !== 'object') return null;
  const value = (body as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : null;
}

/**
 * Which failure this is, in the terms the UI cares about.
 *
 * Driven off the Worker's `code` where there is one, because status alone cannot separate the three
 * things that arrive as 429 — the monthly allowance, the daily safeguard, and the prompt endpoint's
 * hourly rate limit — and they need three different sentences.
 */
export function classifyDreamVisionError(err: unknown): DreamVisionFailure {
  if (!(err instanceof ApiError)) return 'unknown';

  const code = bodyField(err, 'code') ?? bodyField(err, 'error') ?? '';
  switch (code) {
    case 'DREAM_VISION_PAYWALL':
    case 'dream_vision_paywall':
      return 'paywall';
    // Also a 402, and not the same thing at all: the account has generations left, it just cannot
    // have this look. Falling through to the paywall branch would tell someone their free vision
    // was used up when it is still sitting there.
    case 'DREAM_VISION_STYLE_LOCKED':
    case 'dream_vision_style_locked':
      return 'style_locked';
    case 'DREAM_VISION_UNKNOWN_STYLE':
    case 'dream_vision_unknown_style':
      return 'unknown_style';
    case 'DREAM_VISION_MONTHLY_LIMIT':
    case 'dream_vision_monthly_limit':
      return 'monthly_limit';
    case 'DREAM_VISION_DAILY_LIMIT':
    case 'dream_vision_daily_limit':
      return 'daily_limit';
    case 'rate_limited':
      return 'rate_limited';
    case 'image_provider_unconfigured':
    case 'no_key':
      return 'unconfigured';
    case 'image_generation_failed':
    case 'image_store_failed':
    case 'image_record_failed':
      return 'provider';
    default:
      break;
  }

  if (err.status === 0) return 'offline';
  if (err.status === 402 || err.status === 403) return 'paywall';
  if (err.status === 404) return 'missing_source';
  if (err.status === 429) return 'rate_limited';
  if (err.status === 503) return 'unconfigured';
  if (err.status === 502) return 'provider';
  return 'unknown';
}

/** What to actually show. Deliberately says whether a credit was spent, because that is the question. */
export function dreamVisionErrorMessage(err: unknown): string {
  switch (classifyDreamVisionError(err)) {
    case 'paywall':
      return 'Dream Vision is included with Oreia Premium. Your free vision has been used.';
    case 'style_locked':
      return 'That style is part of Oreia Premium. Nothing was charged — your credits are untouched.';
    case 'unknown_style':
      return 'That style is not available. Try again with the standard one.';
    case 'monthly_limit':
      return "You've used every Dream Vision credit this month.";
    case 'daily_limit':
      return "You've reached today's limit of 5 images. It lifts as the last 24 hours roll past.";
    case 'rate_limited':
      return 'Too many requests just now. Wait a minute and try again.';
    case 'unconfigured':
      return 'Image generation is not switched on yet. Nothing was charged.';
    case 'provider':
      return 'The image could not be created. No credit was used — you can try again.';
    case 'missing_source':
      return 'That version is no longer available to recreate from.';
    case 'offline':
      return 'Could not reach Oreia. Check your connection and try again.';
    default:
      return err instanceof ApiError && err.message
        ? err.message
        : 'Something went wrong. Please try again.';
  }
}

/** The renewal date carried on a quota error, if the Worker sent one. */
export function dreamVisionResetsAt(err: unknown): string | null {
  return err instanceof ApiError ? bodyField(err, 'resetsAt') : null;
}
